// =============================================================================
// Module Core - Repository: Onboarding
// Accès DB pour le flux d'onboarding (vérification email, consentement, etc.)
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

// ─── CHECK EMAIL VERIFICATION ────────────────────────────────────────────────

export async function checkEmailVerified(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('core_profiles')
    .select('email_verified_with_code')
    .eq('id', userId)
    .maybeSingle();

  return data?.email_verified_with_code ?? false;
}

// ─── SAVE USER CONSENT ──────────────────────────────────────────────────────

export async function saveUserConsent(
  userId: string,
  consent: { marketing_consent: boolean; terms_consent: boolean; consent_user_agent?: string },
) {
  await supabase.from('core_user_consents').insert({
    user_id: userId,
    marketing_consent: consent.marketing_consent,
    terms_consent: consent.terms_consent,
    consent_user_agent: consent.consent_user_agent,
  });
}

// ─── SEND WELCOME EMAIL ─────────────────────────────────────────────────────

export async function sendWelcomeEmail(email: string, fullName?: string) {
  await supabase.functions.invoke('send-welcome-email', {
    body: { email, fullName },
  });
}

// ─── ACCEPT PENDING INVITATION ──────────────────────────────────────────────

export async function acceptPendingInvitation(token: string) {
  const { data: session } = await supabase.auth.getSession();
  const accessToken = session.session?.access_token;
  if (!accessToken) throw new Error('Session expirée');

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const fetchResponse = await fetch(`${supabaseUrl}/functions/v1/accept-invitation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: supabaseKey,
    },
    body: JSON.stringify({ token }),
  });

  const responseData = await fetchResponse.json();

  if (!fetchResponse.ok) {
    throw new Error(responseData?.error || `HTTP ${fetchResponse.status}`);
  }
  if (responseData?.error) {
    throw new Error(responseData.error);
  }

  return responseData as {
    organization?: { id: string; name: string };
  };
}

// ─── ONBOARDING WIZARD DATA ────────────────────────────────────────────────

export interface OnboardingRecord {
  user_id: string;
  usage_context?: string | null;
  primary_goal?: string | null;
  team_size?: string | null;
  discovery_sources?: string[];
  invited_emails?: string[];
  current_step?: number;
  completed_at?: string;
  skipped_at?: string;
}

export async function getOnboardingRecord(userId: string) {
  const { data } = await supabase
    .from('core_user_onboarding')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();
  return data;
}

/**
 * Returns true when the user actually went through the onboarding wizard
 * (either completed it or explicitly skipped it). Invited users that never
 * went through the /onboarding flow will return false — used to gate the
 * auto-opening of the product tour for true "workspace creators" only.
 */
export async function hasCompletedOnboardingFlow(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('core_user_onboarding')
    .select('completed_at, skipped_at')
    .eq('user_id', userId)
    .maybeSingle();
  return Boolean((data as any)?.completed_at || (data as any)?.skipped_at);
}

export async function upsertOnboardingRecord(userId: string, record: Partial<OnboardingRecord>) {
  const existing = await getOnboardingRecord(userId);
  if (existing) {
    await supabase
      .from('core_user_onboarding')
      .update(record as any)
      .eq('user_id', userId);
  } else {
    await supabase
      .from('core_user_onboarding')
      .insert({ user_id: userId, ...record } as any);
  }
}

// ─── PRODUCT TOUR ───────────────────────────────────────────────────────────

export async function markProductTourCompleted(userId: string) {
  const existing = await getOnboardingRecord(userId);
  if (existing) {
    await supabase
      .from('core_user_onboarding')
      .update({ product_tour_completed: true } as any)
      .eq('user_id', userId);
  } else {
    await supabase
      .from('core_user_onboarding')
      .insert({ user_id: userId, product_tour_completed: true } as any);
  }
}

export async function isProductTourCompleted(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('core_user_onboarding')
    .select('product_tour_completed')
    .eq('user_id', userId)
    .maybeSingle();
  return (data as any)?.product_tour_completed ?? false;
}

